import React, { useState, useEffect, useRef } from "react"
import ContentBrowser from "aldoo-ra/CMS/browsers/content-browser"
import { previewTitle, previewImage } from "aldoo-ra/CMS/preview"
import Tooltip from "aldoo-ra/Tooltip"

export default function GenericContentBrowser() {
  const [urlContentType, setUrlContentType] = useState(null)
  const [urlSearchTerm, setUrlSearchTerm] = useState("")
  const [urlDateRange, setUrlDateRange] = useState(null)
  const skipNextUpdate = useRef(false)
  const lastSelection = useRef(null)

  // Function to read parameters from URL and update state
  const readParamsFromUrl = () => {
    const params = new URLSearchParams(window.location.search)

    // Read type parameter from raw URL to preserve + as separator (URLSearchParams decodes + as space)
    const rawTypeMatch = window.location.search.match(/[?&]type=([^&]*)/)
    const typeParam = rawTypeMatch ? rawTypeMatch[1] : null
    if (typeParam) {
      // Split by '+' to handle multiple types and decode each part
      const types = typeParam.split("+").map((t) => decodeURIComponent(t))

      if (types.length === 1) {
        // Single type case
        setUrlContentType(types[0])
      } else {
        // Multiple types case
        setUrlContentType(types)
      }
    } else {
      setUrlContentType(null)
    }

    // Read search parameter
    const searchParam = params.get("search")
    // Don't decode - URLSearchParams.get() handles decoding automatically
    setUrlSearchTerm(searchParam || "")

    // Read range parameter
    const rangeParam = params.get("range")
    if (rangeParam) {
      try {
        // Don't decode - URLSearchParams.get() handles decoding automatically
        const rangeData = JSON.parse(rangeParam)
        setUrlDateRange(rangeData)
      } catch (error) {
        console.error("Failed to parse range parameter:", error)
        setUrlDateRange(null)
      }
    } else {
      setUrlDateRange(null)
    }
  }

  // Read URL parameters on initial load
  useEffect(() => {
    readParamsFromUrl()

    // Set up an event listener for popstate (browser back/forward)
    const handlePopState = () => {
      skipNextUpdate.current = true
      readParamsFromUrl()
    }

    window.addEventListener("popstate", handlePopState)
    return () => {
      window.removeEventListener("popstate", handlePopState)
    }
  }, [])

  // Helper function to check if a value represents a cleared selection
  const isSelectionCleared = (value) => {
    // Check for explicit null/undefined
    if (value === null || value === undefined) return true

    // Check for empty array
    if (Array.isArray(value) && value.length === 0) return true

    // Check for array with invalid values (common when "deselect all" is used)
    if (
      Array.isArray(value) &&
      value.some(
        (item) =>
          typeof item === "object" &&
          item !== null &&
          "value" in item &&
          typeof item.value === "string" &&
          item.value.includes("=")
      )
    )
      return true

    return false
  }

  // Handle filter changes directly from ContentBrowser
  const handleFilterChange = (filterState) => {
    // If we're processing a URL-based update, skip this update to avoid loops
    if (skipNextUpdate.current) {
      skipNextUpdate.current = false
      return
    }

    // Get current URL components
    const currentPath = window.location.pathname
    const currentParams = new URLSearchParams(window.location.search)

    // Extract the type value from the filter state
    const typeValue = filterState.type

    // When the filter is reset, we get { type: null, filter: {$exists: true} }
    if (typeValue === null || filterState.filter?.$exists === true) {
      // Handle case when selection is cleared
      currentParams.delete("type")

      const queryString = currentParams.toString()
      const newUrl = currentPath + (queryString ? `?${queryString}` : "")

      // Update URL in browser only if different
      if (window.location.href !== newUrl) {
        window.history.pushState({}, "", newUrl)
      }

      // Update state
      lastSelection.current = null
      setUrlContentType(null)
      return
    }

    // Process non-null type values
    if (typeof typeValue === "string") {
      // Single type as string - encode it for URL safety
      const typeParam = encodeURIComponent(typeValue)

      // Update URL
      updateUrlWithParams(currentPath, currentParams, { type: typeParam })

      // Update state
      lastSelection.current = typeValue
      setUrlContentType(typeValue)
    } else if (Array.isArray(typeValue)) {
      // Handle Select All or multiple selection case

      // Extract and normalize values
      const typeValues = typeValue
        .map((t) =>
          typeof t === "object" && t !== null && "value" in t
            ? t.value
            : String(t)
        )
        .filter((v) => typeof v === "string" && !v.includes("="))

      // Deduplicate
      const uniqueValues = [...new Set(typeValues)]

      if (uniqueValues.length === 0) {
        // No valid types after filtering
        currentParams.delete("type")
        const queryString = currentParams.toString()
        const newUrl = currentPath + (queryString ? `?${queryString}` : "")

        window.history.pushState({}, "", newUrl)
        lastSelection.current = null
        setUrlContentType(null)
      } else {
        // We have valid types - encode each value separately and join with +
        const typeParam = uniqueValues
          .map((t) => encodeURIComponent(t))
          .join("+")

        // Update URL
        updateUrlWithParams(currentPath, currentParams, { type: typeParam })

        // Update state based on count
        if (uniqueValues.length === 1) {
          lastSelection.current = uniqueValues[0]
          setUrlContentType(uniqueValues[0])
        } else {
          lastSelection.current = uniqueValues
          setUrlContentType(uniqueValues)
        }
      }
    }
  }

  // Helper to update URL with multiple parameters
  const updateUrlWithParams = (currentPath, currentParams, updates) => {
    // Get all parameters except those being updated
    const paramsToUpdate = Object.keys(updates)
    let otherParams = []
    currentParams.forEach((value, key) => {
      if (!paramsToUpdate.includes(key)) {
        otherParams.push(`${key}=${value}`)
      }
    })

    // Build the URL - manually to preserve special characters like + in type parameter
    const updatedParams = Object.entries(updates)
      .filter(([_, value]) => value !== null && value !== undefined)
      .map(([key, value]) => `${key}=${value}`)

    const queryString = [...updatedParams, ...otherParams].filter(Boolean).join("&")
    const newUrl = currentPath + (queryString ? `?${queryString}` : "")

    if (window.location.href !== newUrl) {
      window.history.pushState({}, "", newUrl)
    }
  }

  // Handle search term changes from ContentBrowser
  const handleSearchChange = (searchTerm) => {
    const currentPath = window.location.pathname
    const currentParams = new URLSearchParams(window.location.search)

    if (searchTerm && searchTerm.trim() !== "") {
      // Don't encode - URLSearchParams.set() handles encoding automatically
      currentParams.set("search", searchTerm)
    } else {
      currentParams.delete("search")
    }

    const queryString = currentParams.toString()
    const newUrl = currentPath + (queryString ? `?${queryString}` : "")

    if (window.location.href !== newUrl) {
      window.history.pushState({}, "", newUrl)
    }
  }

  // Handle date range changes from ContentBrowser
  const handleDateRangeChange = (dateRange) => {
    const currentPath = window.location.pathname
    const currentParams = new URLSearchParams(window.location.search)

    if (dateRange && (dateRange.startDate || dateRange.endDate || dateRange.type)) {
      // Don't encode - URLSearchParams.set() handles encoding automatically
      currentParams.set("range", JSON.stringify(dateRange))
    } else {
      currentParams.delete("range")
    }

    const queryString = currentParams.toString()
    const newUrl = currentPath + (queryString ? `?${queryString}` : "")

    if (window.location.href !== newUrl) {
      window.history.pushState({}, "", newUrl)
    }
  }


  // Legacy handler for backward compatibility
  const handleContentTypeChange = (newContentType) => {
    // If we're processing a URL-based update, skip this update to avoid loops
    if (skipNextUpdate.current) {
      skipNextUpdate.current = false
      return
    }

    // Get current URL components
    const currentPath = window.location.pathname
    const currentParams = new URLSearchParams(window.location.search)

    // Check if selection has been cleared
    if (isSelectionCleared(newContentType)) {
      // Clear the type parameter completely
      currentParams.delete("type")

      const queryString = currentParams.toString()
      const newUrl = currentPath + (queryString ? `?${queryString}` : "")

      // Update URL in browser
      window.history.pushState({}, "", newUrl)

      // Update state
      lastSelection.current = null
      setUrlContentType(null)
      return
    }

    // For other cases, simply update the state
    // URL update will be handled by onFilterChange
    setUrlContentType(newContentType)
  }

  return (
    <ContentBrowser
      contentTitle="Content"
      addButtonText="Add Content"
      localUserPreferencesKey="cms.contentBrowser"
      contentType={urlContentType}
      initialSearchTerm={urlSearchTerm}
      initialDateRange={urlDateRange}
      allowTypeSelection={true}
      allowTypeBrowsing={true}
      showPreview={true}
      searchFields="content.name content.title content.id content.label content.type"
      onContentTypeChange={handleContentTypeChange}
      onFilterChange={handleFilterChange}
      onSearchChange={handleSearchChange}
      onDateRangeChange={handleDateRangeChange}
      additionalColumns={[
        {
          id: "type",
          title: "Type",
          headerClassName: "w-[100px]",
          render: (value, item) => {
            const preview = item ? previewImage(item) : null            
            return (
              <div className="flex flex-row items-center">
                {preview && (
                  <Tooltip
                    content={
                      <div className="text-center">
                        <span className="font-bold text-admin_text">
                          {item?.content?.label || 'Untitled'}
                        </span>
                        <img
                          src={preview}
                          className="w-[400px] h-[400px] object-contain mr-2 rounded-[10px]"
                        />
                      </div>
                    }
                    position="right"
                  >
                    <img
                      src={preview}
                      className="w-10 h-10 mr-2 rounded-[10px]"
                    />
                  </Tooltip>
                )}
                  <div onClick={() => item.onEdit(item)} >
                <span className="text-admin_text hover:text-admin_primary cursor-pointer">{item?.content?.type || 'Unknown'}</span>
                </div>
              </div>
            )
          },
        },
        {
          id: "label",
          title: "ID / Name / Description",
          headerClassName: "w-[55%]",
          render: (value, item) => {
            if (!item) return <span className="text-admin_text">No data</span>;
            
            const title = previewTitle(item);
            // Ensure we never render an object directly
            if (title && typeof title === 'object' && title !== null) {
              const displayText = title.name || title.label || title.value || title.title || title.id || JSON.stringify(title);
              return <span className="text-admin_text">{displayText}</span>;
            }
            
            return <span className="text-admin_text">{title || 'No title'}</span>;
          },
        },
        
      ]}
    />
  )
}
